import { Check } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type RefObject } from 'react'

import { ActionInfoQuiet, Button } from '@krp/ui-kit'

import type { CellKind } from '../model/grid'
import './schedule.css'

const MARGIN = 8

export interface CellMenuProps {
  /** Название меню для диктора: чья клетка и в какой день. */
  title: string
  /** Текущий вид клетки: пункт отмечен галочкой; «Очистить» показывается, только если запись есть. */
  kind: CellKind
  /** Где открыть: точка у курсора (или у клетки при открытии с клавиатуры), в координатах окна. */
  x: number
  y: number
  /** Контейнер прокрутки таблицы: его прокрутка закрывает меню. */
  scroller: RefObject<HTMLElement | null>
  onChoose: (kind: CellKind) => void
  /** Закрыть; `refocus` — вернуть фокус на клетку (Esc и выбор пункта). */
  onClose: (refocus: boolean) => void
}

/** Ставит меню у точки и зажимает в окне; у нижнего или правого края переворачивает к точке с другой стороны. Позицию задаёт сам элемент. */
function place(menu: HTMLElement, x: number, y: number) {
  const bottom = window.innerHeight - MARGIN
  const top = y + menu.offsetHeight > bottom ? y - menu.offsetHeight : y
  menu.style.left = `${Math.max(MARGIN, Math.min(x, window.innerWidth - menu.offsetWidth - MARGIN))}px`
  menu.style.top = `${Math.max(MARGIN, Math.min(top, bottom - menu.offsetHeight))}px`
}

const checkMark = (checked: boolean) => (
  <span className="sch-menu__check" aria-hidden="true">
    {checked && <Check size={14} />}
  </span>
)

/**
 * Контекстное меню клетки: статус одним выбором — «Р», «В», «Н» или «Очистить». Всплывающий слой `fixed` рядом с курсором: таблица
 * его не обрезает и не прокручивается из-за него. Фокус уходит на текущий пункт (или первый); стрелки, Home и End двигают, Enter и
 * Пробел выбирают, Esc закрывает и возвращает фокус клетке. Закрывается и по нажатию вне меню, прокрутке таблицы и смене размера окна.
 */
export function CellMenu({ title, kind, x, y, scroller, onChoose, onClose }: CellMenuProps) {
  const menu = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  }, [onClose])

  useLayoutEffect(() => {
    if (menu.current !== null) place(menu.current, x, y)
  }, [x, y])

  useEffect(() => {
    const root = menu.current
    const start = root?.querySelector<HTMLElement>('[aria-checked="true"]') ?? root?.querySelector<HTMLElement>('[role^="menuitem"]')
    start?.focus()
  }, [])

  // Esc ловится раньше всех: окно, в котором открыт график, от него не закрывается.
  useEffect(() => {
    const scrollRoot = scroller.current
    const outside = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || menu.current?.contains(event.target) !== true) close.current(false)
    }
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      close.current(true)
    }
    const dismiss = () => close.current(false)
    document.addEventListener('pointerdown', outside, true)
    document.addEventListener('keydown', escape, true)
    scrollRoot?.addEventListener('scroll', dismiss)
    window.addEventListener('resize', dismiss)
    return () => {
      document.removeEventListener('pointerdown', outside, true)
      document.removeEventListener('keydown', escape, true)
      scrollRoot?.removeEventListener('scroll', dismiss)
      window.removeEventListener('resize', dismiss)
    }
  }, [scroller])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      onClose(true)
      return
    }
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]'))
    const at = items.findIndex((item) => item === document.activeElement)
    const next =
      event.key === 'ArrowDown' ? (at + 1) % items.length : event.key === 'ArrowUp' ? (at <= 0 ? items.length - 1 : at - 1) : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : -1
    if (next < 0) return
    event.preventDefault()
    items[next]?.focus()
  }

  return (
    <div ref={menu} className="sch-menu" role="menu" aria-label={title} onKeyDown={onKeyDown} onContextMenu={(event) => event.preventDefault()}>
      <ActionInfoQuiet>
        <Button variant="ghost" actionId="schedule.grid.menu-work" role="menuitemradio" aria-checked={kind === 'work'} icon={checkMark(kind === 'work')} onClick={() => onChoose('work')}>
          Р — Выходит
        </Button>
        <Button variant="ghost" actionId="schedule.grid.menu-day-off" role="menuitemradio" aria-checked={kind === 'day_off'} icon={checkMark(kind === 'day_off')} onClick={() => onChoose('day_off')}>
          В — Выходной день
        </Button>
        <Button variant="ghost" actionId="schedule.grid.menu-absent" role="menuitemradio" aria-checked={kind === 'absent'} icon={checkMark(kind === 'absent')} onClick={() => onChoose('absent')}>
          Н — Не выходит
        </Button>
        {kind !== 'none' && (
          <>
            <div className="sch-menu__sep" role="separator" />
            <Button variant="ghost" actionId="schedule.grid.menu-clear" role="menuitem" icon={checkMark(false)} onClick={() => onChoose('none')}>
              Очистить
            </Button>
          </>
        )}
      </ActionInfoQuiet>
    </div>
  )
}
